import { Chip, Group, MultiSelect, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import type { ReactNode } from "react";

import type {
  AiActorAccessory,
  AiActorClothingTag,
  AiActorSituation,
  ListActorsParams,
  SkinTone,
  VoiceAge,
  VoiceGender,
} from "../../api/generated/model";
import {
  AiActorAccessory as ACCESSORIES,
  AiActorSituation as SITUATIONS,
} from "../../api/generated/zod";
import classes from "./actors.module.css";

/** Live's six facets on the global actors, each several values or none (OR within, AND across). */
export interface ActorFilter {
  gender: VoiceGender[];
  age: VoiceAge[];
  skinTone: SkinTone[];
  situation: AiActorSituation[];
  clothing: AiActorClothingTag[];
  accessories: AiActorAccessory[];
}

export const NO_ACTOR_FILTER: ActorFilter = {
  gender: [],
  age: [],
  skinTone: [],
  situation: [],
  clothing: [],
  accessories: [],
};

/** Whether any facet is set: live's dot on the filter button. */
export function filtered(filter: ActorFilter): boolean {
  return Object.values(filter).some((values: unknown[]) => values.length > 0);
}

/** The route's facet parameters: empty ones left out, as live's. */
export function facetParams(filter: ActorFilter): ListActorsParams {
  const many = <K extends string, V>(key: K, values: V[]) =>
    values.length === 0 ? {} : ({ [key]: values } as Record<K, V[]>);
  return {
    ...many("gender", filter.gender),
    ...many("age", filter.age),
    ...many("skin_tone", filter.skinTone),
    ...many("situation", filter.situation),
    ...many("clothing", filter.clothing),
    ...many("accessories", filter.accessories),
  };
}

/** Live's options and words (`constants/global-filters.ts`, `constants/avatar.tsx`). */
const GENDERS: { value: VoiceGender; label: string }[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];
const AGES: { value: VoiceAge; label: string }[] = [
  { value: "kid", label: "Kid" },
  { value: "young_adult", label: "Young Adult" },
  { value: "adult", label: "Adult" },
  { value: "senior", label: "Senior" },
];
const TONES: SkinTone[] = ["Fair", "Light", "Medium", "Tan", "Brown", "Dark"];
const CLOTHING: AiActorClothingTag[] = ["Athletic", "Casual", "Formal", "Business", "Streetwear", "Other"];

export interface ActorFacetsProps {
  value: ActorFilter;
  onChange: (filter: ActorFilter) => void;
}

/**
 * Live's facet fields on the global actors (`global-filters-form`, capture 09 of
 * design-ref/ai-actors and L09 of studio-depth): Gender, Age, Skin Tone (six swatches),
 * Situation, Clothing and Accessories. Each change goes out at once: the page's filter keeps a
 * draft until Apply, the Add Actors dialog applies it as live's sidebar does.
 */
export function ActorFacets({ value, onChange }: ActorFacetsProps) {
  const set = <K extends keyof ActorFilter>(key: K, values: ActorFilter[K]) =>
    onChange({ ...value, [key]: values });
  return (
    <Stack gap="md">
      <Facet label="Gender">
        <Chips options={GENDERS} values={value.gender} onChange={(next) => set("gender", next)} />
      </Facet>
      <Facet label="Age">
        <Chips options={AGES} values={value.age} onChange={(next) => set("age", next)} />
      </Facet>
      <Facet label="Skin Tone">
        <Group gap={6}>
          {TONES.map((tone) => {
            const on = value.skinTone.includes(tone);
            return (
              <Tooltip key={tone} label={tone} withArrow position="top">
                <UnstyledButton
                  className={classes.swatch}
                  data-on={on || undefined}
                  aria-label={tone}
                  aria-pressed={on}
                  onClick={() =>
                    set(
                      "skinTone",
                      on ? value.skinTone.filter((each) => each !== tone) : [...value.skinTone, tone],
                    )
                  }
                >
                  <span className={classes.swatchDot} data-tone={tone.toLowerCase()} />
                </UnstyledButton>
              </Tooltip>
            );
          })}
        </Group>
      </Facet>
      <Facet label="Situation">
        <MultiSelect
          aria-label="Situation"
          placeholder="Select Situation"
          clearable
          data={SITUATIONS.options}
          value={value.situation}
          comboboxProps={{ withinPortal: false }}
          onChange={(next) => set("situation", next as AiActorSituation[])}
        />
      </Facet>
      <Facet label="Clothing">
        <Chips
          options={CLOTHING.map((each) => ({ value: each, label: each }))}
          values={value.clothing}
          onChange={(next) => set("clothing", next)}
        />
      </Facet>
      <Facet label="Accessories">
        <MultiSelect
          aria-label="Accessories"
          placeholder="Select Accessories"
          clearable
          data={ACCESSORIES.options}
          value={value.accessories}
          comboboxProps={{ withinPortal: false }}
          onChange={(next) => set("accessories", next as AiActorAccessory[])}
        />
      </Facet>
    </Stack>
  );
}

function Facet({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={8}>
      <Text size="sm">{label}</Text>
      {children}
    </Stack>
  );
}

interface ChipsProps<V extends string> {
  options: { value: V; label: string }[];
  values: V[];
  onChange: (values: V[]) => void;
}

/** A facet's pills, several at once, each on and off by a click (live's checkbox cards). */
function Chips<V extends string>({ options, values, onChange }: ChipsProps<V>) {
  return (
    <Chip.Group multiple value={values} onChange={(next) => onChange(next as V[])}>
      <Group gap={8}>
        {options.map((each) => (
          <Chip key={each.value} value={each.value}>
            {each.label}
          </Chip>
        ))}
      </Group>
    </Chip.Group>
  );
}
